import React, { Component } from "react";
import Ingredient from "./Ingredient"; // We will create this next
import axios from "axios";
import { v4 as uuid } from "uuid";
const config = require("../config.json"); // Assuming config is in the parent directory

export default class IngredientAdmin extends Component {
  constructor(props) {
    super(props);
    this.handleInputChange = this.handleInputChange.bind(this);
    // Bind other methods if necessary, or use arrow functions
  }

  state = {
    // State for the 'Add New Ingredient' form
    newIngredient: {
      id: uuid(), // Pre-generate ID for new item
      name: "",
      name_en: "",
      category: "",
      image: "image", // New field
      // Nested structure for nutrition and CO2 data is often cleaner
      nutrition: {
        kj: "",
        kcal: "",
        fat: "",
        carbohydrates: "",
        protein: "",
        // Add other nutrition fields if needed
      },
      co2e: {
        totalKg: "",
        agriculture: "",
        iluc: "",
        processing: "",
        packaging: "",
        transport: "",
        detail: "",
      },
      // Add other relevant ingredient fields if any (e.g., unitSourceNote)
      // Remove fields specific to activities like points, weeklySpecial, active, choices, steps etc.
    },
    ingredients: [], // Full list of ingredients fetched from API
    shownIngredients: [], // List currently displayed (can be filtered)
    ingredientCategories: [], // For filtering dropdown (optional)
    ingredientImages: [],
    loading: false,
    loadingImages: false,
  };

  // --- Data Fetching ---

  fetchIngredients = async () => {
    this.setState({ loading: true });
    try {
      const res = await axios.get(
        `${config.api.invokeUrl}/cookbook/ingredients`
      );
      const ingredients = res.data || []; // Ensure it's an array

      // Extract unique categories for filtering (optional)
      const categories = [
        ...new Set(ingredients.map((ing) => ing.category).filter(Boolean)),
      ];

      this.setState({
        ingredients: ingredients,
        shownIngredients: ingredients, // Initially show all
        ingredientCategories: categories,
        loading: false,
      });
    } catch (err) {
      console.error("Error fetching ingredients:", err);
      this.setState({ loading: false });
      // Add user-friendly error handling here
    }
  };

  fetchIngredientImages = async () => {
    // New function
    this.setState({ loadingImages: true });
    try {
      const res = await axios.get(
        `${config.api.invokeUrl}/images?bucketName=${
          config.s3.ingredientImagesBucket ||
          "your-ingredient-images-bucket-name"
        }`
      );
      if (res.data?.Contents) {
        this.setState({
          ingredientImages: res.data.Contents,
          loadingImages: false,
        });
      } else {
        this.setState({ ingredientImages: [], loadingImages: false });
      }
    } catch (err) {
      console.error("Error fetching ingredient images:", err);
      this.setState({ loadingImages: false });
    }
  };

  // --- CRUD Operations ---

  handleAddIngredient = async (ingredientData, event, isCopy = false) => {
    event.preventDefault();
    this.setState({ loading: true });
    const newId = isCopy ? uuid() : ingredientData.id; // Generate new ID if copying

    try {
      // Prepare payload for the backend
      const params = {
        id: newId,
        name: ingredientData.name + (isCopy ? " KOPI" : ""),
        name_en: ingredientData.name_en,
        category: ingredientData.category,
        image: ingredientData.image, // New field
        // Send nested objects directly if backend expects them
        nutrition: {
          kj: Number(ingredientData.nutrition?.kj) || 0,
          kcal: Number(ingredientData.nutrition?.kcal) || 0,
          fat: Number(ingredientData.nutrition?.fat) || 0,
          carbohydrates: Number(ingredientData.nutrition?.carbohydrates) || 0,
          protein: Number(ingredientData.nutrition?.protein) || 0,
        },
        co2e: {
          totalKg: Number(ingredientData.co2e?.totalKg) || 0,
          agriculture: Number(ingredientData.co2e?.agriculture) || 0,
          iluc: Number(ingredientData.co2e?.iluc) || 0,
          processing: Number(ingredientData.co2e?.processing) || 0,
          packaging: Number(ingredientData.co2e?.packaging) || 0,
          transport: Number(ingredientData.co2e?.transport) || 0,
          detail: Number(ingredientData.co2e?.detail) || 0,
        },
        // Include other fields as needed
      };

      // Use POST for creating new item (even if using PUT/POST with ID)
      await axios.post(
        `${config.api.invokeUrl}/cookbook/ingredients/${newId}`, // Or just /ingredients if backend handles ID generation/POST logic
        params
      );

      this.setState({
        newIngredient: {
          // Reset form
          id: uuid(),
          name: "",
          name_en: "",
          category: "",
          image: "",
          nutrition: {
            kj: "",
            kcal: "",
            fat: "",
            carbohydrates: "",
            protein: "",
          },
          co2e: {
            totalKg: "",
            agriculture: "",
            iluc: "",
            processing: "",
            packaging: "",
            transport: "",
            detail: "",
          },
        },
      });

      // Option 2: Refetch (safer, slightly slower UI)
      this.fetchIngredients(); // Call this instead of local update
    } catch (err) {
      console.error("Error adding ingredient:", err);
      this.setState({ loading: false });
      // Add user-friendly error handling
    }
  };

  handleUpdateIngredient = async (ingredientData) => {
    // Note: This function is called from the Ingredient component
    this.setState({ loading: true });
    try {
      // Prepare payload, similar to handleAddIngredient
      const params = {
        id: ingredientData.id, // ID should not change
        name: ingredientData.name,
        name_en: ingredientData.name_en,
        category: ingredientData.category,
        image: ingredientData.image,
        nutrition: {
          kj: Number(ingredientData.nutrition?.kj) || 0,
          kcal: Number(ingredientData.nutrition?.kcal) || 0,
          fat: Number(ingredientData.nutrition?.fat) || 0,
          carbohydrates: Number(ingredientData.nutrition?.carbohydrates) || 0,
          protein: Number(ingredientData.nutrition?.protein) || 0,
        },
        co2e: {
          totalKg: Number(ingredientData.co2e?.totalKg) || 0,
          agriculture: Number(ingredientData.co2e?.agriculture) || 0,
          iluc: Number(ingredientData.co2e?.iluc) || 0,
          processing: Number(ingredientData.co2e?.processing) || 0,
          packaging: Number(ingredientData.co2e?.packaging) || 0,
          transport: Number(ingredientData.co2e?.transport) || 0,
          detail: Number(ingredientData.co2e?.detail) || 0,
        },
        // Include other fields
      };

      await axios.patch(
        // Or PUT
        `${config.api.invokeUrl}/cookbook/ingredients/${ingredientData.id}`,
        params
      );

      // Optimistic UI update or refetch
      const updatedIngredients = this.state.ingredients.map(
        (ing) => (ing.id === ingredientData.id ? { ...params } : ing) // Replace the updated item
      );
      this.setState({
        ingredients: updatedIngredients,
        shownIngredients: this.filterIngredients(
          this.state.selectedCategory, // Re-apply filter
          updatedIngredients
        ),
        loading: false,
      });
      // Or this.fetchIngredients();
    } catch (err) {
      console.error("Error updating ingredient:", err);
      this.setState({ loading: false });
      // Add user-friendly error handling
    }
  };

  handleDeleteIngredient = async (id, event) => {
    event.preventDefault();
    // Use a more specific confirmation message
    if (
      window.confirm(
        `Er du sikker på, at du vil slette denne ingrediens (${id}) permanent?`
      )
    ) {
      this.setState({ loading: true });
      try {
        await axios.delete(
          `${config.api.invokeUrl}/cookbook/ingredients/${id}`
        );

        // Optimistic UI update or refetch
        const updatedIngredients = this.state.ingredients.filter(
          (ing) => ing.id !== id
        );
        this.setState({
          ingredients: updatedIngredients,
          shownIngredients: this.filterIngredients(
            this.state.selectedCategory, // Re-apply filter
            updatedIngredients
          ),
          loading: false,
        });
        // Or this.fetchIngredients();
      } catch (err) {
        console.error("Error deleting ingredient:", err);
        this.setState({ loading: false });
        // Add user-friendly error handling
        alert(err?.response?.data?.message);
      }
    }
  };

  // --- Filtering and Input Handling ---

  filterIngredients = (category, ingredients) => {
    if (category) {
      return ingredients.filter((ing) => ing.category === category);
    } else {
      return ingredients; // No filter applied
    }
  };

  handleFilterChange = (event) => {
    const selectedCategory = event.target.value;
    const filteredIngredients = this.filterIngredients(
      selectedCategory,
      this.state.ingredients
    );

    this.setState({
      shownIngredients: filteredIngredients,
      selectedCategory: selectedCategory,
    });
  };

  // Updated input handler for nested state
  handleInputChange(event) {
    const target = event.target;
    const value = target.type === "checkbox" ? target.checked : target.value; // Keep for potential future use
    const name = target.name;

    if (name.includes(".")) {
      // Handle nested state (e.g., name="nutrition.kcal")
      const [parentKey, childKey] = name.split(".");
      this.setState((prevState) => ({
        newIngredient: {
          ...prevState.newIngredient,
          [parentKey]: {
            ...prevState.newIngredient[parentKey],
            [childKey]: value,
          },
        },
      }));
    } else {
      // Handle top-level state
      this.setState({
        newIngredient: {
          ...this.state.newIngredient,
          [name]: value,
        },
      });
    }
  }

  // --- Lifecycle ---

  componentDidMount() {
    this.fetchIngredients();
    this.fetchIngredientImages();
    // Remove fetchActivitiyImages() call
  }

  // --- Render ---

  render() {
    return (
      <>
        <p className="subtitle is-5">Administrer Ingredienser</p>
        {this.state.loading && <p>Loading...</p>}
        <br />
        <div className="columns is-multiline">
          {/* Column for Adding New Ingredient */}
          <div className="column is-half">
            <form
              onSubmit={(event) =>
                this.handleAddIngredient(this.state.newIngredient, event)
              }
            >
              <h4 className="title is-4">Tilføj Ny Ingrediens</h4>

              {/* Basic Info */}
              <div className="field">
                <label className="label">Navn</label>
                <div className="control">
                  <input
                    name="name"
                    className="input"
                    type="text"
                    placeholder="Indtast ingrediensnavn (fx Hvedemel)"
                    value={this.state.newIngredient.name}
                    onChange={this.handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="field">
                <label className="label">Engelsk navn</label>
                <div className="control">
                  <input
                    name="name_en"
                    className="input"
                    type="text"
                    placeholder="Indtast ingrediensnavn engelsk (fx Flour)"
                    value={this.state.newIngredient.name_en}
                    onChange={this.handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="field">
                <label className="label">Kategori</label>
                <div className="control">
                  {/* Use a text input or a predefined dropdown */}
                  <input
                    name="category"
                    className="input"
                    type="text"
                    placeholder="Indtast kategori (fx Mel, Grøntsag)"
                    value={this.state.newIngredient.category}
                    onChange={this.handleInputChange}
                  />
                </div>
              </div>

              <div className="field">
                <label className="label">Billede</label>
                <div className="control">
                  <div
                    className={`select ${
                      this.state.loadingImages ? "is-loading" : ""
                    }`}
                  >
                    <select
                      name="image"
                      value={this.state.newIngredient.image}
                      onChange={this.handleInputChange}
                      disabled={this.state.loadingImages}
                    >
                      <option value="">Intet billede</option>
                      {this.state.ingredientImages.map((img) => (
                        <option key={img.Key} value={img.Key}>
                          {img.Key}{" "}
                          {/* Display filename, or a more friendly name if available */}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Nutrition Fields */}
              <fieldset className="box">
                <legend className="label">
                  Næringsindhold (typisk pr. 100g)
                </legend>
                <div className="field">
                  <label className="label is-small">kJ</label>
                  <input
                    name="nutrition.kj"
                    type="number"
                    step="any"
                    className="input is-small"
                    value={this.state.newIngredient.nutrition.kj}
                    onChange={this.handleInputChange}
                    placeholder="fx 1515"
                  />
                </div>
                <div className="field">
                  <label className="label is-small">kcal</label>
                  <input
                    name="nutrition.kcal"
                    type="number"
                    step="any"
                    className="input is-small"
                    value={this.state.newIngredient.nutrition.kcal}
                    onChange={this.handleInputChange}
                    placeholder="fx 361"
                  />
                </div>
                <div className="field">
                  <label className="label is-small">Fedt (g)</label>
                  <input
                    name="nutrition.fat"
                    type="number"
                    step="any"
                    className="input is-small"
                    value={this.state.newIngredient.nutrition.fat}
                    onChange={this.handleInputChange}
                    placeholder="fx 1.2"
                  />
                </div>
                <div className="field">
                  <label className="label is-small">Kulhydrat (g)</label>
                  <input
                    name="nutrition.carbohydrates"
                    type="number"
                    step="any"
                    className="input is-small"
                    value={this.state.newIngredient.nutrition.carbohydrates}
                    onChange={this.handleInputChange}
                    placeholder="fx 82"
                  />
                </div>
                <div className="field">
                  <label className="label is-small">Protein (g)</label>
                  <input
                    name="nutrition.protein"
                    type="number"
                    step="any"
                    className="input is-small"
                    value={this.state.newIngredient.nutrition.protein}
                    onChange={this.handleInputChange}
                    placeholder="fx 5.1"
                  />
                </div>
                {/* Add more nutrition inputs here */}
              </fieldset>

              {/* CO2e Fields */}
              <fieldset className="box">
                <legend className="label">
                  CO2e Aftryk (kg CO2e pr. kg produkt)
                </legend>
                <div className="field">
                  <label className="label is-small">Total Kg CO2e</label>
                  <input
                    name="co2e.totalKg"
                    type="number"
                    step="any"
                    className="input is-small"
                    value={this.state.newIngredient.co2e.totalKg}
                    onChange={this.handleInputChange}
                    placeholder="fx 4.93"
                  />
                </div>
                <div className="field">
                  <label className="label is-small">Landbrug</label>
                  <input
                    name="co2e.agriculture"
                    type="number"
                    step="any"
                    className="input is-small"
                    value={this.state.newIngredient.co2e.agriculture}
                    onChange={this.handleInputChange}
                    placeholder="fx 3.99"
                  />
                </div>
                <div className="field">
                  <label className="label is-small">ILUC</label>
                  <input
                    name="co2e.iluc"
                    type="number"
                    step="any"
                    className="input is-small"
                    value={this.state.newIngredient.co2e.iluc}
                    onChange={this.handleInputChange}
                    placeholder="fx 0.16"
                  />
                </div>
                <div className="field">
                  <label className="label is-small">Forarbejdning</label>
                  <input
                    name="co2e.processing"
                    type="number"
                    step="any"
                    className="input is-small"
                    value={this.state.newIngredient.co2e.processing}
                    onChange={this.handleInputChange}
                    placeholder="fx 0.18"
                  />
                </div>
                <div className="field">
                  <label className="label is-small">Emballage</label>
                  <input
                    name="co2e.packaging"
                    type="number"
                    step="any"
                    className="input is-small"
                    value={this.state.newIngredient.co2e.packaging}
                    onChange={this.handleInputChange}
                    placeholder="fx 0.23"
                  />
                </div>
                <div className="field">
                  <label className="label is-small">Transport</label>
                  <input
                    name="co2e.transport"
                    type="number"
                    step="any"
                    className="input is-small"
                    value={this.state.newIngredient.co2e.transport}
                    onChange={this.handleInputChange}
                    placeholder="fx 0.37"
                  />
                </div>
                <div className="field">
                  <label className="label is-small">Detail</label>
                  <input
                    name="co2e.detail"
                    type="number"
                    step="any"
                    className="input is-small"
                    value={this.state.newIngredient.co2e.detail}
                    onChange={this.handleInputChange}
                    placeholder="fx 0.01"
                  />
                </div>
              </fieldset>

              <div className="field">
                <div className="control">
                  <button type="submit" className="button is-primary is-medium">
                    Tilføj Ingrediens
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* Filtering Section (Optional) */}
          <div className="column is-full">
            <hr />
            <h4 className="title is-4">Eksisterende Ingredienser</h4>
            <div className="field">
              <label className="label">Filtrer efter Kategori</label>
              <div className="control">
                <div className="select">
                  <select
                    onChange={this.handleFilterChange}
                    value={this.state.selectedCategory || ""}
                  >
                    <option value="">Alle Kategorier</option>
                    {this.state.ingredientCategories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Display Existing Ingredients */}
        <div className="tile is-ancestor">
          <div className="tile is-parent flex-wrap">
            {this.state.shownIngredients.length > 0
              ? this.state.shownIngredients.map((ingredient) => (
                  <Ingredient // Use the Ingredient component
                    handleUpdateIngredient={this.handleUpdateIngredient}
                    handleDeleteIngredient={this.handleDeleteIngredient}
                    handleAddIngredient={this.handleAddIngredient} // Pass for copy functionality
                    ingredient={ingredient}
                    // Pass down any other necessary props (e.g., categories for dropdown in edit mode)
                    key={ingredient.pk}
                    ingredientImages={this.state.ingredientImages}
                    isAdmin={true} // Assuming this view is always admin
                    className="tile"
                  />
                ))
              : !this.state.loading && <p>Ingen ingredienser fundet.</p>}
          </div>
        </div>
      </>
    );
  }
}
